/**
 * Navigation utilities to prevent double navigation and race conditions
 */

let lastNavigationTime = 0;
let lastNavigationTarget = '';
let isNavigating = false;

// Navigation history to track recent navigations
const navigationHistory = [];
const MAX_HISTORY_SIZE = 10;

/**
 * Safe navigation wrapper that prevents duplicate navigations
 */
export const safeNavigate = (navigate, target, options = {}) => {
  const now = Date.now();
  const normalizedTarget = target.startsWith('/') ? target : `/${target}`;
  
  // Check if this is a duplicate navigation attempt
  if (
    isNavigating ||
    (now - lastNavigationTime < 500 && lastNavigationTarget === normalizedTarget)
  ) {
    console.warn(`🚫 Preventing duplicate navigation to ${normalizedTarget}`);
    return false;
  }

  // Check recent navigation history for rapid navigation attempts
  const recentNavigation = navigationHistory.find(nav => 
    nav.target === normalizedTarget && (now - nav.timestamp < 1000)
  );
  
  if (recentNavigation) {
    console.warn(`🚫 Preventing rapid re-navigation to ${normalizedTarget}`);
    return false;
  }

  // Check if we're already at the target location
  const currentPath = window.location.pathname + window.location.search;
  if (currentPath === normalizedTarget) {
    console.info(`📍 Already at target location: ${normalizedTarget}`);
    return false;
  }

  try {
    isNavigating = true;
    lastNavigationTime = now;
    lastNavigationTarget = normalizedTarget;

    // Add to navigation history
    navigationHistory.push({
      target: normalizedTarget,
      timestamp: now,
      options
    });

    // Keep history size manageable
    if (navigationHistory.length > MAX_HISTORY_SIZE) {
      navigationHistory.shift();
    }

    console.info(`✅ Safe navigation to: ${normalizedTarget}`);
    navigate(normalizedTarget, options);

    // Reset navigation flag after a delay
    setTimeout(() => {
      isNavigating = false;
    }, 500);

    return true;

  } catch (error) {
    console.error(`❌ Navigation failed to ${normalizedTarget}:`, error);
    isNavigating = false;
    return false;
  }
};

/**
 * Check if a navigation is currently in progress
 */
export const isNavigationInProgress = () => isNavigating;

/**
 * Get navigation history for debugging
 */
export const getNavigationHistory = () => [...navigationHistory];

/**
 * Clear navigation history (useful for testing)
 */
export const clearNavigationHistory = () => {
  navigationHistory.length = 0;
  isNavigating = false;
  lastNavigationTime = 0;
  lastNavigationTarget = '';
};

/**
 * Enhanced hook for safe navigation
 */
export const useSafeNavigation = (navigate) => {
  return (target, options = {}) => safeNavigate(navigate, target, options);
}; 